import { useBlocker } from '@tanstack/react-router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import type { MessageId } from '@/shared/i18n/catalogue.en'
import { browserGeneration } from '@/shared/api/productGeneration'
import { useI18n } from '@/shared/i18n/I18nProvider'
import { ConfirmationDialog } from '@/shared/ui/ConfirmationDialog'

/**
 * Unsaved clinical work that only exists in this browser.
 *
 * Nothing here persists anything. The registry's whole job is to know that a
 * clinician has typed something the server has not accepted, so leaving the
 * page can be turned from a silent loss into a decision. Storing the text to
 * make leaving safe would create a second source of truth for clinical
 * content, and the copy that loses is the one they typed into.
 */
export interface UnsavedIntent {
  /**
   * MINTED BY THE HOOK, never supplied by the caller.
   *
   * This is the property the registry turns on. A guard that had to be TOLD
   * which intent it protects can be told the wrong one — two editors sharing a
   * key, or a copied call site keeping the key it was copied from — and the
   * symptom is silent: the wrong work goes unprotected and the screen looks
   * identical. An identity the caller cannot reach has no such failure
   * available.
   */
  readonly id: string
  /**
   * What the clinician is told they would lose. This IS caller-supplied, and
   * being wrong here is a wrong label rather than lost protection: the count,
   * the block and the discard all come from the registry.
   */
  readonly label: MessageId
  /**
   * True for work held by a surface that OUTLIVES in-app navigation — the
   * issue reporter's window rides above the route tree, so walking to another
   * screen loses nothing and the router guard must not ask about it. Leaving
   * the PAGE still loses it: reload, the redeploy hold and sign-out all keep
   * counting a persistent intent.
   */
  readonly persistent: boolean
  /** See `UnsavedWorkKind`. Caller-supplied and required. */
  readonly kind: UnsavedWorkKind
}

/**
 * WHAT THE WORK IS, as opposed to what a prompt would call it.
 *
 * A rule that must act on a KIND of unsaved work — sealing a visit is the one
 * that exists — used to match message identifiers: five of them, listed inside
 * the condition, with a comment saying that each new kind of encounter
 * documentation has to be remembered there. Message ids are chosen for what a
 * sentence should say. Renaming one, splitting one prompt into two, or adding a
 * sixth editor with a new label all left the rule reading a valid id and
 * matching nothing, and the symptom is a visit that seals over unsaved clinical
 * documentation with nothing on screen saying so.
 *
 * So the kind is REQUIRED of every registration rather than defaulted. A
 * default is the same silence with a different spelling: the editor that
 * forgets is exactly the editor that needed the protection, and it would
 * compile. Choosing `other` is a statement that this work is not part of an
 * encounter's record, and it is made where that is known.
 */
export type UnsavedWorkKind =
  /**
   * Work that becomes part of an encounter's record: a protocol, its
   * translation, a diagnosis. Completing the EMR entry over it would seal the
   * visit around something the server has never seen.
   */
  | 'encounter-documentation'
  /** Everything a visit is not sealed around: a form, a message, a task. */
  | 'other'


interface IntentRegistry {
  readonly open: (id: string, label: MessageId, persistent: boolean, kind: UnsavedWorkKind) => void
  readonly close: (id: string) => void
}

const RegistryContext = createContext<IntentRegistry | null>(null)
const IntentsContext = createContext<readonly UnsavedIntent[]>([])

/**
 * Holds the open intents. It is deliberately separate from the guard that acts
 * on them: the registry is neutral bookkeeping, while WHERE the guard is
 * mounted decides what it is allowed to outrank. See `UnsavedIntentGuard`.
 */
export function UnsavedIntentProvider({ children }: { children: ReactNode }) {
  /*
   * A LIST, not a flag.
   *
   * With one boolean, the second editor to go clean releases the first one's
   * protection — the referral form is closed, the note underneath it is still
   * unsaved, and leaving no longer warns. Nothing on screen changes when that
   * happens, which is why it is a list and why `two dirty intents` has its own
   * fixture.
   */
  const [intents, setIntents] = useState<readonly UnsavedIntent[]>([])

  const registry = useMemo<IntentRegistry>(
    () => ({
      open: (id, label, persistent, kind) =>
        setIntents((current) =>
          current.some((intent) => intent.id === id)
            ? current
            : [...current, { id, label, persistent, kind }],
        ),
      close: (id) => setIntents((current) => current.filter((intent) => intent.id !== id)),
    }),
    [],
  )

  return (
    <RegistryContext.Provider value={registry}>
      <IntentsContext.Provider value={intents}>
        {children}
        <ProductGenerationNotice />
      </IntentsContext.Provider>
    </RegistryContext.Provider>
  )
}

function ProductGenerationNotice() {
  const stale = useSyncExternalStore(browserGeneration.subscribe, browserGeneration.isStale)
  if (!stale) return null
  return <StaleProductGenerationNotice />
}

function StaleProductGenerationNotice() {
  const intents = useUnsavedIntents()
  const { t } = useI18n()
  const [confirmReload, setConfirmReload] = useState(false)
  const reload = () => window.location.reload()
  return (
    <>
      <div role="alert" className="error-panel">
        <p>{t('app.generationChanged')}</p>
        <button type="button" className="button" onClick={() => {
          if (intents.length) setConfirmReload(true)
          else reload()
        }}>{t('app.generationReload')}</button>
      </div>
      {confirmReload && <UnsavedWorkPrompt intents={intents} onStay={() => setConfirmReload(false)} onDiscard={reload} />}
    </>
  )
}

/**
 * Declare that THIS editor holds work the server has not accepted.
 *
 * Called by the editor that owns the state, with the same `dirty` the surface
 * already announces on screen. Passing a different one would let the page say
 * "Unsaved" while leaving silently discards it, or the reverse — and a
 * clinician can see that contradiction even though neither half looks wrong on
 * its own.
 */
export function useUnsavedIntent(
  dirty: boolean,
  label: MessageId,
  { kind, persistent = false }: { kind: UnsavedWorkKind; persistent?: boolean },
): void {
  const registry = useContext(RegistryContext)
  const id = useId()
  useEffect(() => {
    if (registry === null || !dirty) {
      return
    }
    registry.open(id, label, persistent, kind)
    return () => registry.close(id)
  }, [registry, dirty, id, label, persistent, kind])
  // Loud, because the alternative is an editor that is simply unprotected and
  // looks exactly like one that is. Thrown after the hooks so the hook order
  // never depends on it. The provider wraps the whole route tree, so this can
  // only fire in a test that renders an editor outside the app.
  if (registry === null) {
    throw new Error('useUnsavedIntent requires an UnsavedIntentProvider above it')
  }
}

/** Every intent currently open. Read by the shell to decide about sign-out. */
export function useUnsavedIntents(): readonly UnsavedIntent[] {
  return useContext(IntentsContext)
}

/**
 * The guard itself: it blocks leaving while any intent is open.
 *
 * WHERE THIS IS MOUNTED IS THE PRIVACY DECISION. It belongs below the shell's
 * session-gone return, so that ending a session unmounts it: `useBlocker`'s
 * cleanup pulls the blocker out of the history in the same commit that blanks
 * the chart, and cleanups run before the effect that issues the redirect. The
 * redirect therefore finds no blocker at all.
 *
 * Mounted next to the provider instead — which reads like tidying — it outlives
 * the blank, and the deregistration it would then depend on is a React state
 * update that has not flushed yet when the redirect is issued. Measured: the
 * redirect is held, and the clinician meets a question about a note on a
 * workstation whose session has already ended. The privacy boundary outranks
 * the confirmation, and it does so structurally rather than by condition.
 */
export function UnsavedIntentGuard() {
  const intents = useUnsavedIntents()
  /*
   * TWO QUESTIONS, TWO COUNTS. Router navigation only loses work held by the
   * route tree, so a persistent intent — the issue reporter, riding above it —
   * must not turn walking to another screen into a question about work the
   * walk does not touch. Unloading the PAGE loses everything, persistent
   * included, so the browser's own prompt counts the whole registry.
   */
  const routeBound = intents.filter((intent) => !intent.persistent)
  const blocking = routeBound.length > 0
  const holding = intents.length > 0
  // Both read the live registry rather than a captured count: `useBlocker`
  // re-registers when these identities change, so the browser and the router
  // are always asking about the intents that are open now.
  const shouldBlockFn = useCallback(() => blocking, [blocking])
  const enableBeforeUnload = useCallback(() => holding, [holding])
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true })

  useEffect(() => {
    // A write can finish while the navigation it triggered is being blocked.
    // Once the registry confirms that no route-bound work remains, continuing
    // that exact pending transition is safe; a prompt naming zero intents is
    // neither a decision nor protection.
    if (blocker.status === 'blocked' && !blocking) blocker.proceed()
  }, [blocker, blocking])

  if (blocker.status !== 'blocked' || !blocking) {
    return null
  }
  return (
    <UnsavedWorkPrompt intents={routeBound} onStay={blocker.reset} onDiscard={blocker.proceed} />
  )
}

/**
 * The question asked before work is lost. It NAMES what would go, all of it:
 * "you have unsaved changes" cannot be answered by someone who does not know
 * whether that means the note, the referral, or both.
 */
export function UnsavedWorkPrompt({
  intents,
  onStay,
  onDiscard,
}: {
  intents: readonly UnsavedIntent[]
  onStay: () => void
  onDiscard: () => void
}) {
  const { t } = useI18n()
  return (
    <ConfirmationDialog
      tone="danger"
      title={t('shell.unsavedTitle')}
      description={t(intents.length === 1 ? 'shell.unsavedBodyOne' : 'shell.unsavedBodyMany', {
        count: intents.length,
      })}
      cancelLabel={t('shell.unsavedStay')}
      confirmLabel={t('shell.unsavedDiscard')}
      onCancel={onStay}
      onConfirm={onDiscard}
      cancelFirst
      cancelVariant="primary"
      confirmVariant="quiet"
    >
      <ul className="modal-dialog__list">
        {intents.map((intent) => (
          <li key={intent.id}>{t(intent.label)}</li>
        ))}
      </ul>
    </ConfirmationDialog>
  )
}
